/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `logger` for the Caintra Connect frontend.
 * @module utils/logger
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
/**
 * Structured logger for client-side code.
 * Callers should use this instead of console.* so browser logs stay
 * JSON-shaped and sensitive fields can be redacted before write.
 */

const SENSITIVE_KEY_PATTERN =
  /(password|passwd|token|secret|authorization|correoElectronico|^email$|userEmail|rfc|presigned|uploadUrl)/i;

const SIGNED_URL_PATTERN =
  /[?&](X-Amz-|AWSAccessKeyId|Signature=|x-amz-security-token)/i;

const redactValue = () => "[REDACTED]";

const redactSignedUrl = (value) => {
  if (typeof value !== "string" || !SIGNED_URL_PATTERN.test(value)) {
    return value;
  }
  const q = value.indexOf("?");
  return q === -1 ? redactValue() : `${value.slice(0, q)}?[REDACTED]`;
};

/**
 * Recursively copy `value` with known PII / secret keys replaced.
 * @param {unknown} value
 * @param {number} [depth]
 * @returns {unknown}
 */
export const sanitize = (value, depth = 0) => {
  if (value === null || value === undefined || depth > 6) {
    return value;
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitize(item, depth + 1));
  }

  if (typeof value === "object") {
    const sanitized = {};
    for (const [key, nested] of Object.entries(value)) {
      sanitized[key] = SENSITIVE_KEY_PATTERN.test(key)
        ? redactValue()
        : sanitize(nested, depth + 1);
    }
    return sanitized;
  }

  return redactSignedUrl(value);
};

/**
 * @param {unknown} error
 * @returns {{ name?: string, message?: string, stack?: string }}
 */
export const sanitizeError = (error) => {
  if (error instanceof Error || (error && typeof error === "object")) {
    return sanitize(error);
  }
  return { message: String(error) };
};

const writeLog = (level, message, meta) => {
  const payload = {
    level,
    message,
    ...(meta && Object.keys(meta).length > 0 ? sanitize(meta) : {}),
  };
  const line = JSON.stringify(payload);
  // Single sink for browser devtools. Callers must not use console.* directly. NOSONAR
  if (level === "error") {
    console.error(line); // NOSONAR
  } else if (level === "warn") {
    console.warn(line); // NOSONAR
  } else {
    console.log(line); // NOSONAR
  }
};

export const logger = {
  debug(message, meta = {}) {
    writeLog("debug", message, meta);
  },
  info(message, meta = {}) {
    writeLog("info", message, meta);
  },
  warn(message, meta = {}) {
    writeLog("warn", message, meta);
  },
  error(message, meta = {}) {
    writeLog("error", message, meta);
  },
};
